// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface BrokerRevenue {
  brokerId: string;
  brokerName: string;
  totalCents: number;
  percentOfTotal: number;
}

interface BrokerConcentrationWidgetProps {
  brokers: BrokerRevenue[];
  threshold?: number; // Minimum percentage to show individually (default 5%)
}

const BROKER_COLORS = [
  '#10B981', // emerald (top broker)
  '#3B82F6', // blue
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#14B8A6', // teal
  '#6B7280', // gray (other)
];

const BrokerConcentrationWidget: React.FC<BrokerConcentrationWidgetProps> = ({
  brokers,
  threshold = 5,
}) => {
  // Prepare pie chart data - group small brokers as "Other"
  const pieData = useMemo(() => {
    if (!brokers || brokers.length === 0) return [];

    const significant = brokers.filter(b => b.percentOfTotal >= threshold);
    const otherBrokers = brokers.filter(b => b.percentOfTotal < threshold);

    const data = significant.map(b => ({
      name: b.brokerName,
      value: b.totalCents,
      percentOfTotal: b.percentOfTotal,
    }));

    if (otherBrokers.length > 0) {
      const otherTotal = otherBrokers.reduce((sum, b) => sum + b.totalCents, 0);
      const otherPercent = otherBrokers.reduce((sum, b) => sum + b.percentOfTotal, 0);

      data.push({
        name: 'Other',
        value: otherTotal,
        percentOfTotal: otherPercent,
      });
    }

    return data;
  }, [brokers, threshold]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2">{data.name}</p>
          <p className="text-sm" style={{ color: data.payload.fill }}>
            Revenue: ${(data.value / 100).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {data.payload.percentOfTotal.toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    if (!payload || payload.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-3 justify-center mt-4">
        {payload.map((entry: any, index: number) => {
          const isTopBroker = index === 0 && entry.value !== 'Other';
          return (
            <div
              key={`legend-${index}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                isTopBroker ? 'bg-green-500/10 border border-green-500/30' : 'bg-gray-800'
              }`}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-white font-medium">
                {entry.value}
                {isTopBroker && (
                  <span className="ml-1.5 text-xs text-green-400 font-semibold">
                    (Top)
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-400 ml-1">
                {entry.payload?.percentOfTotal?.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  if (!pieData || pieData.length === 0) {
    return (
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
             style={{ background: 'radial-gradient(circle at 50% 0%, #10b98140 0%, transparent 70%)' }}
        />
        <h3 className="font-bold text-lg mb-4 text-white relative z-10">
          Broker Revenue Distribution
        </h3>
        <div className="p-8 bg-gray-700 rounded text-center relative z-10">
          <p className="text-gray-400">No broker revenue data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10"
           style={{ background: 'radial-gradient(circle at 50% 0%, #10b98140 0%, transparent 70%)' }}
      />

      <div className="relative z-10">
        <h3 className="font-bold text-lg mb-2 text-white">
          Broker Revenue Distribution
        </h3>
        <p className="text-sm text-gray-400 mb-6">
          Concentration analysis of broker contributions to total revenue
        </p>

        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={BROKER_COLORS[index % BROKER_COLORS.length]}
                  stroke="#1F2937"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Summary stats */}
        <div className="mt-6 pt-4 border-t border-gray-700 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Brokers</p>
            <p className="text-xl font-bold text-white">{brokers.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Top Broker</p>
            <p className="text-xl font-bold text-green-400">
              {brokers.length > 0 ? `${brokers[0].percentOfTotal.toFixed(1)}%` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Avg Per Broker</p>
            <p className="text-xl font-bold text-blue-400">
              {brokers.length > 0 ? `${(100 / brokers.length).toFixed(1)}%` : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerConcentrationWidget;
